/**
 * SERVICIO DE COMPRAS DE PAQUETES
 * 
 * Este módulo maneja todas las operaciones relacionadas con la compra de paquetes de licencias.
 * Se comunica con el backend FastAPI para:
 * - Obtener paquetes disponibles
 * - Crear registros de compra
 * 
 * Endpoints utilizados:
 * - GET /api/admin/compras/paquetes - Lista de paquetes disponibles
 * - POST /api/admin/compras/paquetes - Crear una compra
 */

// URL base del API del backend
// Usa REACT_APP_API_BASE_URL_RENDER para desarrollo remoto o local según configuración en .env
const BASE_URL = process.env.REACT_APP_API_BASE_URL_RENDER || process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const API_BASE_URL = `${BASE_URL}/api`;

/**
 * Servicio de compras
 */
const comprasService = {
    /**
     * Obtiene el token de autenticación desde localStorage
     * @returns {string|null} Token de acceso o null si no existe
     */
    getAuthToken() {
        return localStorage.getItem('access_token');
    },

    /**
     * Obtiene los headers necesarios para las peticiones autenticadas
     * @returns {Object} Headers con autenticación
     */
    getAuthHeaders() {
        const token = this.getAuthToken();
        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        };
    },

    /**
     * Decodifica un JWT sin verificar la firma (solo para debugging)
     * @returns {Object|null} Payload del token o null si falla
     */
    decodeToken() {
        try {
            const token = this.getAuthToken();
            if (!token) return null;

            // Dividir el token en sus partes: header.payload.signature
            const parts = token.split('.');
            if (parts.length !== 3) return null;

            // Decodificar el payload (segunda parte)
            const payload = JSON.parse(atob(parts[1]));
            return payload;
        } catch (error) {
            console.error('Error al decodificar token:', error);
            return null;
        }
    },

    /**
     * Obtiene el rol del usuario desde el token JWT
     * @returns {string|null} Rol del usuario o null si no existe
     */
    getUserRole() {
        try {
            const payload = this.decodeToken();
            return payload?.role || payload?.user_role || null;
        } catch (error) {
            console.error('Error al obtener rol del usuario:', error);
            return null;
        }
    },

    /**
     * Muestra información completa del usuario en consola (para debugging)
     */
    debugUserInfo() {
        console.group('🔍 DEBUG: Información del Usuario');
        
        // Token
        const token = this.getAuthToken();
        console.log('📝 Token existe:', !!token);
        if (token) {
            console.log('📝 Token (primeros 50 chars):', token.substring(0, 50) + '...');
        }

        // Token decodificado
        const tokenPayload = this.decodeToken();
        console.log('🔓 Token decodificado:', tokenPayload);

        // Rol del token
        const role = this.getUserRole();
        console.log('👤 Rol del token:', role);

        // Usuario del localStorage
        const userStr = localStorage.getItem('user');
        if (userStr) {
            const user = JSON.parse(userStr);
            console.log('💾 Usuario localStorage:', user);
            console.log('🏢 ID Empresa:', user.companyData?.id_empresa);
            console.log('📧 Email:', user.email);
        } else {
            console.log('❌ No hay usuario en localStorage');
        }

        console.groupEnd();
    },

    /**
     * Obtiene el ID de la empresa del usuario actual desde localStorage
     * @returns {string|null} ID de la empresa o null si no existe
     */
    getCurrentCompanyId() {
        try {
            const userStr = localStorage.getItem('user');
            if (!userStr) return null;

            const user = JSON.parse(userStr);
            return user.companyData?.id_empresa || null;
        } catch (error) {
            console.error('Error al obtener ID de empresa:', error);
            return null;
        }
    },

    /**
     * Obtiene la lista de paquetes disponibles para compra
     * 
     * @returns {Promise<Array>} Array de paquetes con estructura:
     *   - id: number
     *   - nombre: string
     *   - precio: number
     *   - cant_licencias: number
     * 
     * @throws {Error} Si falla la petición al servidor
     * 
     * @example
     * const paquetes = await comprasService.getPaquetes();
     * // Retorna: [
     * //   { id: 1, nombre: "Plan Básico", precio: 99000, cant_licencias: 10 },
     * //   { id: 2, nombre: "Plan Intermedio", precio: 219000, cant_licencias: 50 },
     * //   ...
     * // ]
     */
    async getPaquetes() {
        try {
            // 🔍 DEBUG: Mostrar información del usuario
            console.log('🔐 Obteniendo paquetes - Verificando autenticación...');
            this.debugUserInfo();

            const url = `${API_BASE_URL}/admin/compras/paquetes`;
            
            const response = await fetch(url, {
                method: 'GET',
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                console.error('❌ Error del servidor:', errorData);
                throw new Error(errorData.detail || 'Error al obtener paquetes');
            }

            const data = await response.json();
            console.log('✅ Paquetes obtenidos exitosamente:', data.length, 'paquetes');
            return data; // El backend retorna directamente el array
        } catch (error) {
            console.error('❌ Error al obtener paquetes:', error);
            throw error;
        }
    },

    /**
     * Crea una nueva compra de paquete
     * 
     * @param {Object} compraData - Datos de la compra
     * @param {number} compraData.paquete_id - ID del paquete a comprar
     * @param {number} compraData.cant_paquetes - Cantidad de paquetes (normalmente 1)
     * @param {string} compraData.id_empresa - ID de la empresa compradora (opcional, usa la actual si no se proporciona)
     * 
     * @returns {Promise<Object>} Datos de la compra creada con estructura:
     *   - pago_id: string (UUID)
     *   - estado: string ("PENDIENTE")
     *   - monto: number
     *   - mensaje: string
     * 
     * @throws {Error} Si falla la petición al servidor o faltan datos
     * 
     * @example
     * const resultado = await comprasService.crearCompra({
     *   paquete_id: 1,
     *   cant_paquetes: 1,
     *   id_empresa: "abc-123"
     * });
     */
    async crearCompra(compraData) {
        try {
            // Validar datos requeridos
            if (!compraData.paquete_id) {
                throw new Error('El ID del paquete es requerido');
            }

            if (!compraData.cant_paquetes || compraData.cant_paquetes < 1) {
                throw new Error('La cantidad de paquetes debe ser mayor a 0');
            }

            // Si no se proporciona id_empresa, usar la del usuario actual
            const id_empresa = compraData.id_empresa || this.getCurrentCompanyId();
            
            if (!id_empresa) {
                throw new Error('No se pudo obtener el ID de la empresa');
            }

            const url = `${API_BASE_URL}/admin/compras/paquetes`;
            
            const payload = {
                paquete_id: compraData.paquete_id,
                cant_paquetes: compraData.cant_paquetes,
                id_empresa: id_empresa
            };

            console.log('📦 Creando compra:', payload);
            //imprimir rol en consola
            
            const response = await fetch(url, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || 'Error al crear la compra');
            }

            const data = await response.json();
            console.log('✅ Compra creada:', data);
            
            return data;
        } catch (error) {
            console.error('❌ Error al crear compra:', error);
            throw error;
        }
    },

    /**
     * Mapea un paquete del backend al formato esperado por el frontend
     * 
     * @param {Object} paquete - Paquete del backend
     * @returns {Object} Paquete con formato del frontend
     */
    mapPaqueteToFrontend(paquete) {
        // Mapear ID a color y icono
        const colorMap = {
            1: { color: 'info', icon: 'workspace_premium' },
            2: { color: 'success', icon: 'stars' },
            3: { color: 'warning', icon: 'diamond' },
        };

        const config = colorMap[paquete.id] || { color: 'primary', icon: 'local_offer' };

        return {
            id: paquete.id,
            nombre: paquete.nombre,
            precio: paquete.precio,
            tests: paquete.cant_licencias,
            descripcion: this.getDescripcionPorPaquete(paquete.id),
            color: config.color,
            icon: config.icon
        };
    },

    /**
     * Obtiene una descripción basada en el ID del paquete
     * (Temporal hasta que el backend incluya descripciones)
     * 
     * @param {number} paqueteId - ID del paquete
     * @returns {string} Descripción del paquete
     */
    getDescripcionPorPaquete(paqueteId) {
        const descripciones = {
            1: 'Ideal para pequeñas empresas',
            2: 'Para empresas medianas con procesos de selección regulares',
            3: 'Ideal para empresas grandes con alto volumen de evaluaciones'
        };

        return descripciones[paqueteId] || 'Paquete de licencias para evaluaciones';
    }
};

export default comprasService;
